import {
  Box,
  ContainerProps,
  Container as MuiContainer,
  styled,
  Tab,
  TabProps,
  Tabs,
  TabsProps,
} from "@mui/material";
import kebabCase from "lodash/kebabCase";
import { ReactNode } from "react";
import { Link } from "react-router";

type SectionNavigationItem<T> = {
  value: T;
  label: string;
  path: string;
  onClick?: () => void;
} & Pick<TabProps, "iconPosition" | "icon">;

interface Props<T> {
  value: T;
  prefix?: ReactNode;
  extra?: ReactNode;
  navItems: SectionNavigationItem<T>[];
  slotProps?: Partial<{
    container: Partial<ContainerProps>;
    tabs: Partial<TabsProps>;
  }>;
}

const SectionNavigation = <T,>({
  prefix,
  navItems,
  extra,
  value,
  slotProps = {},
}: Props<T>): React.JSX.Element => (
  <Container data-testid="ds-section-navigation" {...slotProps.container}>
    {prefix && <div data-testid="ds-section-navigation-prefix">{prefix}</div>}
    <Box flexGrow={1} display="flex" justifyContent="flex-start">
      <Tabs
        data-testid="ds-section-navigation-tabs"
        value={value}
        onChange={(_, newValue) => navItems[newValue]?.onClick?.()}
        aria-label="primary navigation"
        role="navigation"
        {...slotProps.tabs}
      >
        {navItems?.map((item) => (
          <Tab
            data-testid={`ds-section-navigation-tab-${kebabCase(item.label)}`}
            key={item.label}
            label={item.label}
            icon={item.icon}
            aria-label={item.label}
            component={Link}
            value={item.value}
            to={item.path}
            iconPosition={item.iconPosition}
          />
        ))}
      </Tabs>
    </Box>

    {extra && (
      <Box
        data-testid="ds-section-navigation-extra"
        display="flex"
        alignItems="right"
        gap={1}
        flexShrink={0}
      >
        {extra}
      </Box>
    )}
  </Container>
);

export const Container = styled(MuiContainer)(() => ({
  ".MuiTabs-indicator": {
    height: 2,
  },
})) as unknown as typeof MuiContainer;

export { SectionNavigation };
export type { SectionNavigationItem };
